import { useMutation } from "@tanstack/react-query"
import { queryClient, removeFailedMutationsForEntity } from "@/lib/queryClient"
import { PhotoEntityType, PhotoRecord } from "./useUploadPhoto"
import { psUpdate } from "@/lib/powersync/crud"

/**
 * Payload for updating a photo caption.
 * Contains all data needed for the mutation and for optimistic updates.
 */
export type UpdatePhotoCaptionPayload = {
  id: string
  entity_id: string
  entity_type: PhotoEntityType
  caption: string | null
}

/**
 * Mutation key for photo caption update - used for registry lookup.
 */
export const UPDATE_PHOTO_CAPTION_MUTATION_KEY = ["photos", "updateCaption"]

/**
 * Check if a photo has a pending caption update mutation.
 */
export const getIsUpdatePhotoCaptionPending = (photoId: string): boolean => {
  const mutations = queryClient.getMutationCache().findAll({
    mutationKey: UPDATE_PHOTO_CAPTION_MUTATION_KEY,
    predicate: ({ state }) => {
      const isPendingOrPaused =
        state.status === "pending" || Boolean(state.isPaused)

      if (!isPendingOrPaused) return false

      const variables = state.variables as UpdatePhotoCaptionPayload | undefined
      return variables?.id === photoId
    },
  })
  return Boolean(mutations.length)
}

/**
 * Cancel and remove any pending caption update mutations for a specific photo.
 */
export const cancelUpdatePhotoCaption = (photoId: string): void => {
  const mutations = queryClient.getMutationCache().findAll({
    mutationKey: UPDATE_PHOTO_CAPTION_MUTATION_KEY,
    predicate: ({ state }) => {
      const variables = state.variables as UpdatePhotoCaptionPayload | undefined
      return variables?.id === photoId
    },
  })

  for (const mutation of mutations) {
    queryClient.getMutationCache().remove(mutation)
  }
}

/**
 * The mutation function for updating a photo caption.
 * Exported separately so it can be registered in the mutation registry
 * for rehydration after app restart.
 *
 * For survey_site photos: writes to PowerSync local SQLite.
 * For observation photos: still uses direct Supabase calls.
 */
export async function updatePhotoCaptionFn(
  payload: UpdatePhotoCaptionPayload,
): Promise<PhotoRecord> {
  const { id: photoId, caption } = payload

  await psUpdate("photos", photoId, {
    caption,
    updated_at: new Date().toISOString(),
  })
  // Return a partial record — PowerSync auto-invalidates queries
  return { id: photoId, caption } as PhotoRecord
}

/**
 * Success handler for photo caption update.
 * Exported so it can be registered in mutation defaults for rehydration.
 */
export const onSuccessUpdatePhotoCaption = async (
  data: PhotoRecord,
  payload: UpdatePhotoCaptionPayload,
) => {
  const { entity_id, entity_type } = payload

  // Update the cache with the real data
  queryClient.setQueriesData<PhotoRecord[]>(
    { queryKey: ["photos", entity_type, entity_id] },
    (oldData) => {
      if (!oldData || oldData.length === 0) return []
      return oldData.map((item) => (item.id === data.id ? data : item))
    },
  )

  // Invalidate to ensure fresh data
  await queryClient.invalidateQueries({
    queryKey: ["photos", entity_type, entity_id],
  })
}

/**
 * Hook for updating a photo caption.
 * Supports offline persistence and rehydration.
 *
 * @param entityId - The ID of the entity (observation or survey_site) the photo belongs to.
 *   Used to set the mutation scope so updates are sequenced with other entity mutations.
 */
export const useUpdatePhotoCaption = (entityId: string) => {
  return useMutation({
    mutationKey: UPDATE_PHOTO_CAPTION_MUTATION_KEY,
    mutationFn: updatePhotoCaptionFn,
    networkMode: "always",
    scope: { id: `entity-${entityId}` },
    onMutate: async (payload: UpdatePhotoCaptionPayload) => {
      const { id: photoId, entity_id, entity_type, caption } = payload

      removeFailedMutationsForEntity(photoId)

      // Optimistically update the caption
      queryClient.setQueriesData<PhotoRecord[]>(
        { queryKey: ["photos", entity_type, entity_id] },
        (oldData) => {
          if (!oldData || oldData.length === 0) return []
          return oldData.map((item) =>
            item.id === photoId ? { ...item, caption } : item,
          )
        },
      )
    },
    onSuccess: onSuccessUpdatePhotoCaption,
  })
}
